import { useState, useEffect } from "react";
import axiosInstance from "../../../../axiosInstance";
import "./ShiftForm.scss";
import { toast } from "react-toastify";
import { Building, Users, CheckSquare, Square } from "lucide-react";

const ShiftForm = ({ selectedShift, onCancel, onSuccess }) => {
  const convertTo24Hour = (time12h) => {
    if (!time12h) return "";
    const [time, modifier] = time12h.split(" ");
    let [hours, minutes] = time.split(":");
    if (hours === "12") {
      hours = "00";
    }
    if (modifier.toLowerCase() === "pm") {
      hours = parseInt(hours, 10) + 12;
    }
    return `${hours.toString().padStart(2, "0")}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    shiftName: selectedShift?.shiftName || "",
    shiftForm: selectedShift?.shiftForm
      ? convertTo24Hour(selectedShift.shiftForm)
      : "",
    shiftTo: selectedShift?.shiftTo
      ? convertTo24Hour(selectedShift.shiftTo)
      : "",
    branchIds: selectedShift?.branches?.map((b) => b.branchId) || [],
    selectAll: false,
  });

  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (selectedShift && branches.length > 0) {
      const allBranchIds = branches.map((branch) => branch.branchId);
      const selectedBranchIds =
        selectedShift.branches?.map((b) => b.branchId) || [];

      if (
        allBranchIds.length === selectedBranchIds.length &&
        allBranchIds.every((id) => selectedBranchIds.includes(id))
      ) {
        setFormData((prev) => ({ ...prev, selectAll: true }));
      }
    }
  }, [selectedShift, branches]);

  useEffect(() => {
    if (formData.selectAll) {
      setFormData((prev) => ({
        ...prev,
        branchIds: branches.map((branch) => branch.branchId),
      }));
    } else if (!selectedShift) {
      setFormData((prev) => ({
        ...prev,
        branchIds: [],
      }));
    }
  }, [formData.selectAll, branches, selectedShift]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Using the new single endpoint that provides all the data
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/branches/enabled/list-with-departments`
      );

      if (response.status === 200) {
        const branchesData = response.data.data;

        if (Array.isArray(branchesData)) {
          setBranches(branchesData);
        } else {
          console.error(
            "Unexpected structure for branches data:",
            branchesData
          );
          setBranches([]);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", {
        message: error.message,
        response: error.response
          ? {
              status: error.response.status,
              statusText: error.response.statusText,
              data: error.response.data,
            }
          : null,
        request: error.request
          ? {
              url: error.request.responseURL,
              method: error.request.method,
            }
          : null,
      });
      setBranches([]);
      toast.error("Failed to load branches. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatTime12Hour = (hour, minute) => {
    let formattedHour = hour;
    const suffix = formattedHour >= 12 ? "pm" : "am";
    if (formattedHour > 12) {
      formattedHour -= 12;
    } else if (formattedHour === 0) {
      formattedHour = 12;
    }
    const formattedMinute = String(minute).padStart(2, "0");
    return `${String(formattedHour).padStart(
      2,
      "0"
    )}:${formattedMinute} ${suffix}`;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleBranchSelect = (branchId) => {
    setFormData((prev) => {
      const newBranchIds = prev.branchIds.includes(branchId)
        ? prev.branchIds.filter((id) => id !== branchId)
        : [...prev.branchIds, branchId];
      const selectAll = newBranchIds.length === branches.length;
      return { ...prev, branchIds: newBranchIds, selectAll };
    });
  };

  const handleSelectAllBranches = () => {
    const allBranchIds = branches.map((branch) => branch.branchId);
    const allSelected = allBranchIds.every((id) =>
      formData.branchIds.includes(id)
    );
    if (allSelected) {
      setFormData((prev) => ({ ...prev, branchIds: [], selectAll: false }));
    } else {
      setFormData((prev) => ({
        ...prev,
        branchIds: allBranchIds,
        selectAll: true,
      }));
    }
  };

  const handleSubmitShift = async (event) => {
    event.preventDefault();

    if (!formData.shiftName.trim()) {
      toast.error("Please enter a shift name");
      return;
    }
    if (!formData.shiftForm || !formData.shiftTo) {
      toast.error("Please select both start and end times");
      return;
    }
    if (formData.branchIds.length === 0) {
      toast.error("Please select at least one branch");
      return;
    }

    const formattedShiftForm = formatTime12Hour(
      Number(formData.shiftForm?.split(":")[0]) || 0,
      Number(formData.shiftForm?.split(":")[1]) || 0
    );
    const formattedShiftTo = formatTime12Hour(
      Number(formData.shiftTo?.split(":")[0]) || 0,
      Number(formData.shiftTo?.split(":")[1]) || 0
    );

    const payload = {
      officeTimingId: selectedShift ? selectedShift.officeTimingId : 0,
      shiftName: formData.shiftName,
      shiftForm: formattedShiftForm,
      shiftTo: formattedShiftTo,
      status: selectedShift?.status || "ENABLED",
      companyId: parseInt(process.env.REACT_APP_COMPANY_ID),
      branchIds: formData.branchIds,
    };

    try {
      let response;
      if (selectedShift) {
        response = await axiosInstance.put(
          `${process.env.REACT_APP_API_BASE_URL}/office-timings/${selectedShift.officeTimingId}`,
          payload
        );
        toast.success("Shift updated successfully");
      } else {
        response = await axiosInstance.post(
          `${process.env.REACT_APP_API_BASE_URL}/office-timings`,
          payload
        );
        toast.success("Shift created successfully");
      }

      if (response.status === 200 || response.status === 201) {
        if (onSuccess) {
          onSuccess();
        }
      } else {
        console.error("Error submitting office timing:", response.data);
      }
    } catch (error) {
      if (error.response?.data?.status === 409) {
        toast.error(error.response.data.message);
      } else {
        toast.error("An error occurred while saving the shift");
      }
      console.error("Error submitting office timing:", error);
    }
  };

  const allBranchIds = branches.map((branch) => branch.branchId);
  const allBranchesSelected =
    allBranchIds.length > 0 &&
    allBranchIds.every((id) => formData.branchIds.includes(id));

  const renderBranchesSection = () => {
    if (isLoading) {
      return (
        <div className="shift-form__loading">
          <div className="shift-form__loading-spinner"></div>
          <span>Loading branches...</span>
        </div>
      );
    }

    if (branches.length === 0) {
      return (
        <div className="text-center py-16 rounded-2xl border-2 border-dashed border-gray-300">
          <Building className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No Active Branches
          </h3>
          <p className="text-gray-600 max-w-md mx-auto">
            There are no active branches available at the moment. Please check
            back later or contact your administrator.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building className="h-6 w-6 text-blue-600" />
            <h3 className="text-xl font-semibold text-gray-900">
              Select Branches
            </h3>
            <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2 py-2.5 rounded-full">
              {formData.branchIds.length} of {branches.length} selected
            </span>
          </div>
          {branches.length > 0 && (
            <button
              type="button"
              onClick={handleSelectAllBranches}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-[20px] transition-colors duration-200"
            >
              {allBranchesSelected ? (
                <>
                  <CheckSquare className="h-4 w-4" />
                  Deselect All
                </>
              ) : (
                <>
                  <Square className="h-4 w-4" />
                  Select All
                </>
              )}
            </button>
          )}
        </div>
        <div className="grid sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-2">
          {branches.map((branch) => (
            <div
              key={branch.branchId}
              className="p-3 rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all duration-200"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-sm text-gray-900">
                  {branch.branchName}
                </span>
                <button
                  type="button"
                  onClick={() => handleBranchSelect(branch.branchId)}
                  className={`p-1 rounded-md transition-colors ${
                    formData.branchIds.includes(branch.branchId)
                      ? "bg-blue-100 text-blue-600"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  {formData.branchIds.includes(branch.branchId) ? (
                    <CheckSquare className="h-4 w-4" />
                  ) : (
                    <Square className="h-4 w-4" />
                  )}
                </button>
              </div>

              <div className="flex items-center gap-1 text-xs text-gray-600 mb-2">
                <Users className="h-3 w-3" />
                <span>{branch.departments?.length || 0} departments</span>
              </div>

              {/* Display existing office timings for this branch */}
              {branch.officeTimings && branch.officeTimings.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <div className="text-xs text-gray-500 mb-1">
                    Current shifts:
                  </div>
                  <div className="space-y-1">
                    {branch.officeTimings.map((timing) => (
                      <div
                        key={timing.officeTimingId}
                        className="text-xs bg-gray-50 px-2 py-1 rounded"
                      >
                        <div className="font-medium">{timing.shiftName}</div>
                        <div className="text-gray-600">{timing.offTime}h)</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="shift-form-container w-full p-8 bg-white rounded-2xl shadow-lg border border-gray-100 space-y-10">
      <form className="shift-form" onSubmit={handleSubmitShift}>
        <div className="shift-form__header">
          <h2 className="shift-form__title">
            {selectedShift ? "Edit Shift" : "Create New Shift"}
          </h2>
          <p className="shift-form__subtitle">
            {selectedShift
              ? "Update the shift details below"
              : "Fill in the details to create a new shift"}
          </p>
        </div>
        <div className="shift-form__content">
          <div className="shift-form__section">
            <h3 className="shift-form__section-title">Shift Information</h3>
            <div className="shift-form__field shift-form__field--full-width">
              <label className="shift-form__label">
                Shift Name *
                <input
                  type="text"
                  value={formData.shiftName}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 placeholder-gray-500"
                  placeholder="E.g: Morning Shift"
                  onChange={(e) => handleChange("shiftName", e.target.value)}
                  required
                />
              </label>
            </div>
            <div className="shift-form__row">
              <div className="shift-form__field">
                <label className="shift-form__label">
                  Start Time *
                  <input
                    type="time"
                    value={formData.shiftForm}
                    onChange={(e) => handleChange("shiftForm", e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 placeholder-gray-500"
                    required
                  />
                </label>
              </div>
              <div className="shift-form__field">
                <label className="shift-form__label">
                  End Time *
                  <input
                    type="time"
                    value={formData.shiftTo}
                    onChange={(e) => handleChange("shiftTo", e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 placeholder-gray-500"
                    required
                  />
                </label>
              </div>
            </div>
          </div>
          <div className="shift-form__section">
            <h3 className="shift-form__section-title">Branch Selection</h3>
            <p className="shift-form__section-description">
              Select which branches this shift applies to. You can see existing
              shifts for each branch.
            </p>
            {renderBranchesSection()}
          </div>
        </div>
        <div className="shift-form__actions">
          <button
            type="button"
            onClick={onCancel}
            className="shift-form__btn shift-form__btn--cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="shift-form__btn shift-form__btn--save"
            disabled={isLoading || branches.length === 0}
          >
            {selectedShift ? "Update Shift" : "Create Shift"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ShiftForm;
